import type { ArenaBounds, Hazard, Player } from "./models";

export class HazardSystem {
  update(
    hazard: Hazard,
    deltaSeconds: number,
    bounds: ArenaBounds,
    speedScale = 1,
    frozen = false,
    player?: Player,
  ): void {
    if (frozen) return;
    hazard.age = (hazard.age ?? 0) + deltaSeconds;
    if (hazard.type === "laser") {
      const cycle = hazard.age % 4.5;
      hazard.phase = cycle < 1.25 ? "warning" : cycle < 2.75 ? "active" : "cooldown";
      if (hazard.phase === "active") {
        hazard.position.x += hazard.velocity.x * deltaSeconds * speedScale;
        hazard.position.y += hazard.velocity.y * deltaSeconds * speedScale;
      }
      this.clampAndBounce(hazard, bounds);
      return;
    }
    if (hazard.type === "mine") {
      const cycle = hazard.age % 4;
      hazard.phase = cycle < 2 ? "warning" : cycle < 2.7 ? "active" : "cooldown";
      return;
    }
    if (hazard.type === "jammer") {
      hazard.phase = "active";
      return;
    }
    if (hazard.type === "hunter") {
      const cycle = hazard.age % 5;
      hazard.phase = cycle < 2 ? "warning" : cycle < 2.65 ? "active" : "cooldown";
      return;
    }
    if (hazard.type === "tracker" && player) {
      const dx = player.position.x - hazard.position.x;
      const dy = player.position.y - hazard.position.y;
      const length = Math.hypot(dx, dy) || 1;
      const speed = Math.hypot(hazard.velocity.x, hazard.velocity.y) || 50;
      const desiredX = dx / length * speed;
      const desiredY = dy / length * speed;
      hazard.velocity.x += (desiredX - hazard.velocity.x) * Math.min(1, deltaSeconds * 1.4);
      hazard.velocity.y += (desiredY - hazard.velocity.y) * Math.min(1, deltaSeconds * 1.4);
    }
    hazard.position.x += hazard.velocity.x * deltaSeconds * speedScale;
    hazard.position.y += hazard.velocity.y * deltaSeconds * speedScale;
    this.clampAndBounce(hazard, bounds);
  }

  private clampAndBounce(hazard: Hazard, bounds: ArenaBounds): void {
    if (hazard.position.x <= bounds.left || hazard.position.x >= bounds.right) {
      hazard.position.x = Math.max(bounds.left, Math.min(bounds.right, hazard.position.x));
      hazard.velocity.x *= -1;
    }
    if (hazard.position.y <= bounds.top || hazard.position.y >= bounds.bottom) {
      hazard.position.y = Math.max(bounds.top, Math.min(bounds.bottom, hazard.position.y));
      hazard.velocity.y *= -1;
    }
  }

  overlaps(player: Player, hazard: Hazard): boolean {
    if (
      (hazard.type === "laser" || hazard.type === "mine" || hazard.type === "hunter")
      && hazard.phase !== "active"
    ) {
      return false;
    }
    if (hazard.type === "jammer") return false;
    if (hazard.type === "laser" || hazard.type === "hunter") {
      const halfLength = (hazard.length ?? 240) / 2;
      if (hazard.orientation === "vertical") {
        return Math.abs(player.position.x - hazard.position.x) < player.radius + hazard.radius
          && Math.abs(player.position.y - hazard.position.y) < halfLength + player.radius;
      }
      return Math.abs(player.position.y - hazard.position.y) < player.radius + hazard.radius
        && Math.abs(player.position.x - hazard.position.x) < halfLength + player.radius;
    }
    return Math.hypot(
      player.position.x - hazard.position.x,
      player.position.y - hazard.position.y,
    ) < player.radius + hazard.radius;
  }

  insideJammer(player: Player, hazards: Hazard[]): boolean {
    return hazards.some((hazard) =>
      hazard.type === "jammer"
      && Math.hypot(
        player.position.x - hazard.position.x,
        player.position.y - hazard.position.y,
      ) < player.radius + hazard.radius);
  }
}
